import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Save,
  Download,
  Share2,
  Sparkles,
  ArrowLeft,
  Copy,
  Eye,
  EyeOff,
  FileDown,
  MessageSquare,
  Lightbulb,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Undo,
  Redo,
  StickyNote as StickyNoteIcon,
  Palette,
  Users,
  Wifi,
  WifiOff,
  X,
  Check,
} from "lucide-react";
import { diagrams as diagramsApi, ai as aiApi } from "@/lib/api";
import { parseDSL } from "@/lib/dsl-parser";
import DiagramRenderer from "@/components/editor/DiagramRenderer";
import { StickyNote } from "@/components/editor/StickyNote";
import { NodeColorPicker } from "@/components/editor/NodeColorPicker";
import { useAuth } from "@/lib/auth-context";
import { useCollaboration } from "@/hooks/useCollaboration";
import CodeMirror from "@uiw/react-codemirror";
import dslLanguage from "@/lib/codemirror-dsl";

const DIAGRAM_TYPES = [
  { value: "agent-workflow", label: "Agent Workflow" },
  { value: "ai-agent", label: "AI Agent" },
  { value: "block", label: "Block" },
  { value: "c4", label: "C4" },
  { value: "class", label: "Class" },
  { value: "dag", label: "DAG" },
  { value: "er", label: "ER" },
  { value: "flowchart", label: "Flowchart" },
  { value: "gantt", label: "Gantt" },
  { value: "git", label: "Git" },
  { value: "journey", label: "Journey" },
  { value: "kanban", label: "Kanban" },
  { value: "knowledge-graph", label: "Knowledge Graph" },
  { value: "mindmap", label: "Mindmap" },
  { value: "model-arch", label: "Model Architecture" },
  { value: "packet", label: "Packet" },
  { value: "pie", label: "Pie" },
  { value: "pipeline", label: "Pipeline" },
  { value: "quadrant", label: "Quadrant" },
  { value: "requirements", label: "Requirements" },
  { value: "sankey", label: "Sankey" },
  { value: "sequence", label: "Sequence" },
  { value: "state", label: "State" },
  { value: "system-arch", label: "System Architecture" },
  { value: "timeline", label: "Timeline" },
];

const DEFAULT_TEMPLATES = {
  flowchart: `graph TD
  A[Start] --> B[Process]
  B --> C{Decision}
  C -->|Yes| D[Result]
  C -->|No| E[Alternative]`,
  sequence: `sequence
  Client ->> Server: HTTP Request
  Server ->> Database: Query
  Database -->> Server: Results
  Server -->> Client: Response`,
  class: `class
  class Animal {
    +name: string
    +age: int
    +makeSound(): void
  }
  class Dog {
    +breed: string
    +fetch(): void
  }
  class Cat {
    +color: string
    +purr(): void
  }
  Animal <|-- Dog
  Animal <|-- Cat`,
  architecture: `graph TD
  Client[Client App] --> Gateway[API Gateway]
  Gateway --> Auth[Auth Service]
  Gateway --> Users[User Service]
  Gateway --> Orders[Order Service]
  Auth --> AuthDB[(Auth DB)]
  Users --> UserDB[(User DB)]
  Orders --> OrderDB[(Order DB)]`,
  gantt: `gantt
  title Project Timeline
  section Design
    Wireframes: 2024-01-01, 5d
    Mockups: 2024-01-06, 3d
  section Development
    Frontend: 2024-01-09, 10d
    Backend: 2024-01-09, 12d
  section Testing
    QA Testing: 2024-01-21, 5d
    Bug Fixes: 2024-01-26, 3d`,
  kanban: `kanban
  column Backlog
    Research competitors
    Define requirements
  column Todo
    Design homepage
    Create API endpoints
  column In Progress
    Build auth system
  column Done
    Setup project
    Configure CI/CD`,
  quadrant: `quadrant
  title Priority Matrix
  x-axis Low Effort --> High Effort
  y-axis Low Impact --> High Impact
  quadrant-1 Quick Wins
  quadrant-2 Major Projects
  quadrant-3 Fill-Ins
  quadrant-4 Thankless Tasks
  Dark Mode: [0.2, 0.8]
  AI Integration: [0.8, 0.9]
  Fix Typos: [0.1, 0.2]
  Refactor DB: [0.9, 0.3]
  Add Tooltips: [0.3, 0.6]
  Mobile App: [0.7, 0.7]`,
  requirements: `requirements
  req User Authentication {
    id: REQ-001
    priority: high
    status: approved
  }
  req Password Reset {
    id: REQ-002
    priority: medium
    status: pending
  }
  req OAuth Integration {
    id: REQ-003
    priority: low
    status: planned
  }
  REQ-001 - derives -> REQ-002
  REQ-001 - contains -> REQ-003`,
  journey: `journey
  title User Registration Flow
  section Discovery
    Visit homepage: 5: User
    Read features: 4: User
  section Registration
    Click sign up: 5: User
    Fill in form: 3: User
    Verify email: 2: User
  section Onboarding
    Complete profile: 4: User
    Create first project: 5: User`,
  pie: `pie
  title Browser Market Share
  "Chrome" : 65
  "Safari" : 18
  "Firefox" : 8
  "Edge" : 6
  "Other" : 3`,
  mindmap: `mindmap
  Project Planning
    Research
      Market Analysis
      Competitor Review
    Design
      Wireframes
      Mockups
      Prototypes
    Development
      Frontend
      Backend
      Database
    Testing
      Unit Tests
      Integration
      UAT`,
  sankey: `sankey
  title Energy Flow
  Coal,Electricity,100
  Gas,Electricity,50
  Nuclear,Electricity,30
  Electricity,Industry,80
  Electricity,Residential,60
  Electricity,Commercial,40`,
  timeline: `timeline
  title Company Milestones
  section 2020
    Jan 2020 : Company Founded
    Jun 2020 : First Product Launch
  section 2021
    Mar 2021 : Series A Funding
    Sep 2021 : 100K Users
  section 2022
    Feb 2022 : International Expansion
    Nov 2022 : 1M Users`,
  state: `state
  [*] --> Idle
  Idle --> Processing : start
  Processing --> Completed : success
  Processing --> Error : failure
  Error --> Idle : retry
  Completed --> [*]`,
  er: `erDiagram
  USERS {
    int id PK
    string name
    string email UK
  }
  ORDERS {
    int id PK
    int user_id FK
    decimal total
  }
  PRODUCTS {
    int id PK
    string name
    decimal price
  }
  USERS ||--o{ ORDERS : places
  ORDERS ||--|{ PRODUCTS : contains`,
  block: `block
  block Frontend "Frontend Layer"
    React Components
    State Management
    API Client
  block Backend "Backend Layer"
    FastAPI Server
    Business Logic
    Data Access
  block Database "Data Layer"
    MongoDB
    Redis Cache`,
  c4: `c4
  title System Context
  Person(user, "User", "End user of the system")
  System(webapp, "Web Application", "Main application")
  System(api, "API Server", "Backend services")
  Database(db, "Database", "PostgreSQL")
  Rel(user, webapp, "Uses")
  Rel(webapp, api, "Makes API calls")
  Rel(api, db, "Reads/writes")`,
  git: `git
  commit id: "init" msg: "Initial commit"
  commit id: "feat1" msg: "Add feature"
  branch develop
  commit id: "dev1" msg: "Dev work"
  checkout main
  commit id: "fix1" msg: "Hotfix"
  checkout develop
  merge main`,
  packet: `packet
  title TCP Header
  "Source Port" : 16
  "Destination Port" : 16
  "Sequence Number" : 32
  "Acknowledgment" : 32
  "Data Offset" : 4
  "Reserved" : 6
  "Flags" : 6
  "Window Size" : 16`,
  "ai-agent": `agent
  title Autonomous AI Agent
  [user] UserInput "User request"
  [agent] Orchestrator "Main agent"
  [llm] GPT4 "Language model"
  [memory] ShortTerm "Working memory"
  [vectordb] LongTerm "Vector store"
  [tool] WebSearch "Search tool"
  [tool] CodeExec "Code executor"
  [api] ExternalAPI "External services"
  UserInput --> Orchestrator : prompt
  Orchestrator <--> GPT4 : reasoning
  Orchestrator <--> ShortTerm : context
  Orchestrator <--> LongTerm : retrieve/store
  Orchestrator --> WebSearch : search query
  Orchestrator --> CodeExec : run code
  CodeExec --> ExternalAPI : http requests`,
  "agent-workflow": `agentWorkflow
  title ReAct Agent Loop
  [start] Start
  [task] Receive "Receive Task"
  [task] Think "Think & Plan"
  [decision] NeedTool "Need Tool?"
  [task] SelectTool "Select Tool"
  [task] Execute "Execute Tool"
  [task] Observe "Observe Result"
  [decision] Done "Task Done?"
  [task] Respond "Generate Response"
  [end] End
  Start --> Receive
  Receive --> Think
  Think --> NeedTool
  NeedTool --> SelectTool : yes
  NeedTool --> Respond : no
  SelectTool --> Execute
  Execute --> Observe
  Observe --> Think : loop
  Done --> Respond : yes
  Respond --> End`,
  dag: `dag
  title ML Training DAG
  task DataIngest "Data Ingestion" [success]
  task Validate "Data Validation" [success]
  task Preprocess "Preprocessing" [running]
  task FeatureEng "Feature Engineering" [pending]
  task TrainModel "Train Model" [pending]
  task Evaluate "Evaluate Model" [pending]
  task Deploy "Deploy Model" [pending]
  DataIngest --> Validate
  Validate --> Preprocess
  Preprocess --> FeatureEng
  FeatureEng --> TrainModel
  TrainModel --> Evaluate
  Evaluate --> Deploy`,
  pipeline: `pipeline
  title RAG Inference Pipeline
  [input] Query "User Query"
  [transform] Embed "Embed Query"
  [process] Retrieve "Vector Retrieve"
  [process] Rerank "Rerank Chunks"
  [model] LLM "LLM Generation"
  [output] Response "Final Response"
  Query --> Embed
  Embed --> Retrieve : vector search
  Retrieve --> Rerank : top-k docs
  Rerank --> LLM : context + query
  LLM --> Response`,
  "knowledge-graph": `knowledgeGraph
  title AI Concepts Graph
  entity LLM [concept] "Large Language Model"
  entity Transformer [concept] "Transformer Architecture"
  entity Attention [concept] "Attention Mechanism"
  entity BERT [model]
  entity GPT [model]
  entity FineTuning [process]
  entity RAG [pattern]
  LLM -[based on]-> Transformer
  Transformer -[uses]-> Attention
  BERT -[is a]-> LLM
  GPT -[is a]-> LLM
  LLM -[enhanced by]-> FineTuning
  LLM -[used in]-> RAG`,
  "model-arch": `modelArch
  title Transformer Model
  layer Input [embedding] "Input Embedding (512)"
  layer PosEnc [embedding] "Positional Encoding"
  layer Attn1 [attention] "Multi-Head Attention"
  layer Norm1 [activation] "Layer Norm"
  layer FFN [dense] "Feed-Forward Network (2048)"
  layer Norm2 [activation] "Layer Norm"
  layer Attn2 [attention] "Decoder Attention"
  layer Output [dense] "Linear + Softmax"
  Input --> PosEnc
  PosEnc --> Attn1
  Attn1 --> Norm1
  Norm1 --> FFN
  FFN --> Norm2
  Norm2 --> Attn2
  Attn2 --> Output`,
  "system-arch": `systemArch
  title AI Platform Architecture
  layer frontend
  [service] WebApp "Web Application"
  [service] CLI "CLI Tool"
  layer backend
  [service] API "REST API"
  [service] AgentService "Agent Service"
  [service] Scheduler "Task Scheduler"
  layer ai
  [model] LLMGateway "LLM Gateway"
  [model] EmbedModel "Embedding Model"
  layer data
  [database] VectorDB "Vector Store"
  [database] PostgreSQL "Relational DB"
  [database] Redis "Cache"
  layer external
  [api] OpenAI "OpenAI API"
  [api] Storage "Cloud Storage"
  WebApp --> API
  CLI --> API
  API --> AgentService
  AgentService --> LLMGateway
  AgentService --> EmbedModel
  EmbedModel --> VectorDB
  LLMGateway --> OpenAI
  API --> PostgreSQL
  API --> Redis`,
};

const DEFAULT_CODE = DEFAULT_TEMPLATES.flowchart;

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [code, setCode] = useState(DEFAULT_CODE);
  const [title, setTitle] = useState("Untitled Diagram");
  const [diagramType, setDiagramType] = useState("flowchart");
  const [isPublic, setIsPublic] = useState(false);
  const [diagramId, setDiagramId] = useState(id || null);
  const [parsedGraph, setParsedGraph] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Undo/Redo state
  const [history, setHistory] = useState([DEFAULT_CODE]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const editorRef = useRef(null);

  // AI state
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMode, setAiMode] = useState("generate");
  const [aiResult, setAiResult] = useState("");
  const [aiHistory, setAiHistory] = useState({ explain: "", improve: "" }); // persist last results
  const [showCodePanel, setShowCodePanel] = useState(true);

  // Export & Share
  const [showExport, setShowExport] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  // Node colors & Annotations (sticky notes)
  const [nodeColors, setNodeColors] = useState({});
  const [annotations, setAnnotations] = useState([]);
  const [colorPicker, setColorPicker] = useState(null); // { nodeId, x, y }
  const [addNoteMode, setAddNoteMode] = useState(false);
  const canvasRef = useRef(null);

  // Collaboration state
  const [collabEnabled, setCollabEnabled] = useState(false);
  const [showCollabPanel, setShowCollabPanel] = useState(false);
  const [collabCanEditNotes, setCollabCanEditNotes] = useState(true);
  const token = useMemo(() => localStorage.getItem("auth_token"), []);

  // Called when a remote user changes the code
  const handleRemoteCodeChange = useCallback((remoteCode, senderSessionId) => {
    setCode(remoteCode);
  }, []);

  // Called when remote user changes annotations/colors
  const handleRemoteAnnotationsChange = useCallback((remoteAnnotations, remoteNodeColors) => {
    setAnnotations(remoteAnnotations || []);
    setNodeColors(remoteNodeColors || {});
  }, []);

  const { peers, sessionId, connected, permissions, sendCodeChange, sendAnnotationsChange, sendPermissionUpdate } = useCollaboration({
    diagramId: diagramId,
    enabled: collabEnabled && !!diagramId,
    token,
    onRemoteCodeChange: handleRemoteCodeChange,
    onRemoteAnnotationsChange: handleRemoteAnnotationsChange,
  });

  // Sync annotations/nodeColors to remote peers
  const syncAnnotations = useCallback((ann, nc) => {
    if (collabEnabled) sendAnnotationsChange(ann, nc);
  }, [collabEnabled, sendAnnotationsChange]);

  // Node color handler
  const handleNodeClick = useCallback((nodeId, clientX, clientY) => {
    if (!addNoteMode) {
      setColorPicker({ nodeId, x: clientX, y: clientY });
    }
  }, [addNoteMode]);

  const handleColorSelect = useCallback((color) => {
    if (!colorPicker) return;
    const newColors = { ...nodeColors, [colorPicker.nodeId]: color };
    setNodeColors(newColors);
    setColorPicker(null);
    syncAnnotations(annotations, newColors);
    // Auto-save if diagram exists
    if (diagramId && user) {
      diagramsApi.updateAnnotations(diagramId, { annotations, node_colors: newColors }).catch(() => {});
    }
  }, [colorPicker, nodeColors, annotations, diagramId, user, syncAnnotations]);

  // Sticky note handlers
  const handleCanvasClick = useCallback((e) => {
    if (!addNoteMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const newNote = {
      id: `note_${Date.now()}`,
      type: "sticky",
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      text: "",
      color: "#fef08a",
      node_id: null,
    };
    const newAnnotations = [...annotations, newNote];
    setAnnotations(newAnnotations);
    setAddNoteMode(false);
    syncAnnotations(newAnnotations, nodeColors);
    if (diagramId && user) {
      diagramsApi.updateAnnotations(diagramId, { annotations: newAnnotations, node_colors: nodeColors }).catch(() => {});
    }
  }, [addNoteMode, annotations, nodeColors, diagramId, user, syncAnnotations]);

  const handleNoteUpdate = useCallback((updated) => {
    const newAnnotations = annotations.map(a => a.id === updated.id ? updated : a);
    setAnnotations(newAnnotations);
    syncAnnotations(newAnnotations, nodeColors);
    if (diagramId && user) {
      diagramsApi.updateAnnotations(diagramId, { annotations: newAnnotations, node_colors: nodeColors }).catch(() => {});
    }
  }, [annotations, nodeColors, diagramId, user, syncAnnotations]);

  const handleNoteDelete = useCallback((noteId) => {
    const newAnnotations = annotations.filter(a => a.id !== noteId);
    setAnnotations(newAnnotations);
    syncAnnotations(newAnnotations, nodeColors);
    if (diagramId && user) {
      diagramsApi.updateAnnotations(diagramId, { annotations: newAnnotations, node_colors: nodeColors }).catch(() => {});
    }
  }, [annotations, nodeColors, diagramId, user, syncAnnotations]);

  // Parse DSL on code change
  useEffect(() => {
    try {
      const parsed = parseDSL(code);
      setParsedGraph(parsed);
      setParseError(null);
      if (parsed) setDiagramType(parsed.type);
    } catch (e) {
      setParseError(e.message);
    }
  }, [code]);

  // Handle code change with history tracking
  const handleCodeChange = useCallback((newCode) => {
    setCode(newCode);
    // Broadcast to collaborators
    if (collabEnabled) sendCodeChange(newCode);
    // Add to history (debounced to avoid too many history entries)
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      if (newHistory[newHistory.length - 1] !== newCode) {
        return [...newHistory, newCode];
      }
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex, collabEnabled, sendCodeChange]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCode(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCode(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Load existing diagram
  useEffect(() => {
    if (id) {
      diagramsApi
        .get(id)
        .then((res) => {
          setCode(res.data.dsl_code);
          setTitle(res.data.title);
          setDiagramType(res.data.diagram_type);
          setIsPublic(res.data.is_public);
          setDiagramId(res.data.id);
          setNodeColors(res.data.node_colors || {});
          setAnnotations(res.data.annotations || []);
          setCollabCanEditNotes(res.data.collab_can_edit_notes !== false); // default true
          // Auto-enable collab so owner is in the room
          setCollabEnabled(true);
        })
        .catch(() => {
          toast.error("Failed to load diagram");
          navigate("/dashboard");
        });
    }
  }, [id, navigate]);

  // Load template from navigation state
  useEffect(() => {
    if (location.state?.code) {
      setCode(location.state.code);
      if (location.state.title) setTitle(location.state.title);
    }
    if (location.state?.type && DEFAULT_TEMPLATES[location.state.type]) {
      setCode(DEFAULT_TEMPLATES[location.state.type]);
      setDiagramType(location.state.type);
    }
  }, [location.state]);

  // Save
  const handleSave = async () => {
    if (!user) {
      toast.error("Please sign in to save diagrams");
      navigate("/auth");
      return;
    }
    setSaving(true);
    try {
      if (diagramId) {
        await diagramsApi.update(diagramId, {
          title,
          dsl_code: code,
          diagram_type: diagramType,
          is_public: isPublic,
          node_colors: nodeColors,
          annotations,
        });
        toast.success("Diagram saved");
      } else {
        const res = await diagramsApi.create({ title, dsl_code: code, diagram_type: diagramType });
        const newId = res.data.id;
        setDiagramId(newId);
        navigate(`/editor/${newId}`, { replace: true });
        // Save annotations/colors for newly created diagram
        if (annotations.length > 0 || Object.keys(nodeColors).length > 0) {
          await diagramsApi.updateAnnotations(newId, { annotations, node_colors: nodeColors }).catch(() => {});
        }
        toast.success("Diagram created");
      }
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  // AI Generate / Explain / Improve
  const handleAIAction = async (modeOverride) => {
    const mode = modeOverride || aiMode;
    if (!user) {
      toast.error("Please sign in to use AI features");
      navigate("/auth");
      return;
    }
    if (mode === "generate" && !aiPrompt.trim()) return;
    setAiLoading(true);
    setAiResult("");
    try {
      if (mode === "generate") {
        const res = await aiApi.generate({ prompt: aiPrompt, diagram_type: diagramType });
        setCode(res.data.dsl_code);
        setShowAI(false);
        setAiPrompt("");
        toast.success("Diagram generated!");
      } else if (mode === "explain") {
        const res = await aiApi.explain({ dsl_code: code });
        const result = res.data.explanation || "";
        setAiResult(result);
        setAiHistory(prev => ({ ...prev, explain: result }));
      } else if (mode === "improve") {
        const res = await aiApi.improve({ dsl_code: code });
        const result = res.data.suggestions || "";
        setAiResult(result);
        setAiHistory(prev => ({ ...prev, improve: result }));
        if (res.data.improved_dsl) {
          setCode(res.data.improved_dsl);
          toast.success("Diagram improved!");
        }
      }
    } catch {
      toast.error("AI request failed. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  // Export
  const handleExport = async (format) => {
    try {
      if (format === "svg") {
        const svgEl = document.querySelector("#diagram-svg");
        if (!svgEl) { toast.error("No diagram to export"); return; }
        const svgData = new XMLSerializer().serializeToString(svgEl);
        downloadBlob(new Blob([svgData], { type: "image/svg+xml" }), `${title}.svg`);
      } else if (format === "png") {
        const { toPng } = await import("html-to-image");
        const el = document.querySelector("[data-testid='diagram-canvas']");
        if (!el) { toast.error("No diagram to export"); return; }
        const dataUrl = await toPng(el, { backgroundColor: "#fafbfc" });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `${title}.png`;
        a.click();
      } else if (format === "pdf") {
        const { toPng } = await import("html-to-image");
        const el = document.querySelector("[data-testid='diagram-canvas']");
        if (!el) { toast.error("No diagram to export"); return; }
        const dataUrl = await toPng(el, { backgroundColor: "#ffffff" });
        const { jsPDF } = await import("jspdf");
        const img = new Image();
        img.src = dataUrl;
        img.onload = () => {
          const pdf = new jsPDF({ orientation: img.width > img.height ? "landscape" : "portrait", unit: "px", format: [img.width, img.height] });
          pdf.addImage(dataUrl, "PNG", 0, 0, img.width, img.height);
          pdf.save(`${title}.pdf`);
        };
      } else if (format === "json") {
        downloadBlob(
          new Blob([JSON.stringify(parsedGraph, null, 2)], { type: "application/json" }),
          `${title}.json`
        );
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
      setShowExport(false);
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Share
  const handleToggleShare = async () => {
    if (!user || !diagramId) {
      toast.error("Save the diagram first");
      return;
    }
    const newPublic = !isPublic;
    try {
      await diagramsApi.update(diagramId, { is_public: newPublic });
      setIsPublic(newPublic);
      toast.success(newPublic ? "Diagram is now public" : "Diagram is now private");
    } catch {
      toast.error("Failed to update sharing");
    }
  };

  const shareUrl = diagramId
    ? `${window.location.origin}/view/${diagramId}`
    : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const embedCode = `<iframe src="${window.location.origin}/embed/${diagramId}" width="800" height="600" frameborder="0" style="border:1px solid #e2e8f0;border-radius:8px"></iframe>`;

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setEmbedCopied(true);
    toast.success("Embed code copied!");
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  return (
    <div className="h-screen flex flex-col bg-white" data-testid="editor-page">
      {/* Toolbar */}
      <div className="h-12 border-b border-slate-200 flex items-center px-3 gap-2 bg-white/80 backdrop-blur-xl shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate(user ? "/dashboard" : "/")}
          data-testid="editor-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="h-5 w-px bg-slate-200" />
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="max-w-[220px] h-8 text-sm font-medium border-transparent hover:border-slate-200 focus:border-slate-300"
          data-testid="diagram-title-input"
        />
        <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded text-slate-500 uppercase tracking-wider font-medium hidden md:inline">
          {diagramType}
        </span>
        <Select
          value={diagramType}
          onValueChange={(val) => {
            if (DEFAULT_TEMPLATES[val]) {
              setCode(DEFAULT_TEMPLATES[val]);
            }
            setDiagramType(val);
          }}
        >
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="diagram-type-selector">
            <SelectValue placeholder="Diagram Type" />
          </SelectTrigger>
          <SelectContent>
            {DIAGRAM_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`type-${t.value}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />

        {/* Undo/Redo */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleUndo}
          disabled={historyIndex <= 0}
          title="Undo (Ctrl+Z)"
          data-testid="undo-btn"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleRedo}
          disabled={historyIndex >= history.length - 1}
          title="Redo (Ctrl+Shift+Z)"
          data-testid="redo-btn"
        >
          <Redo className="h-4 w-4" />
        </Button>
        <div className="h-5 w-px bg-slate-200" />

        {/* Show/Hide Code panel */}
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-8 text-xs"
          onClick={() => setShowCodePanel(v => !v)}
          title={showCodePanel ? "Hide code editor" : "Show code editor"}
          data-testid="toggle-code-panel-btn"
        >
          {showCodePanel ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showCodePanel ? "Hide Code" : "Show Code"}
        </Button>

        {/* AI Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="ai-menu-btn">
              <Sparkles className="h-3.5 w-3.5" /> AI <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => { setAiMode("generate"); setAiResult(""); setShowAI(true); }}
              data-testid="ai-generate-option"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Generate from Text
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { setAiMode("explain"); setShowAI(true); handleAIAction("explain"); }}
              data-testid="ai-explain-option"
            >
              <MessageSquare className="h-4 w-4 mr-2" /> Explain Diagram
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { setAiMode("improve"); setShowAI(true); handleAIAction("improve"); }}
              data-testid="ai-improve-option"
            >
              <Lightbulb className="h-4 w-4 mr-2" /> Suggest Improvements
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setShowExport(true)} data-testid="export-btn">
          <Download className="h-3.5 w-3.5" /> Export
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setShowShare(true)} data-testid="share-btn">
          <Share2 className="h-3.5 w-3.5" /> Share
        </Button>

        {/* Sticky note button */}
        <Button
          variant={addNoteMode ? "default" : "outline"}
          size="sm"
          className="gap-1.5 h-8 text-xs"
          onClick={() => { setAddNoteMode(v => !v); setColorPicker(null); }}
          title="Add sticky note (click canvas to place)"
          data-testid="add-note-btn"
        >
          <StickyNoteIcon className="h-3.5 w-3.5" />
          {addNoteMode ? "Click canvas..." : "Note"}
        </Button>

        {/* Collaboration button */}
        <Button
          variant={collabEnabled ? "default" : "outline"}
          size="sm"
          className="gap-1.5 h-8 text-xs"
          onClick={() => {
            if (!diagramId) { toast.error("Save the diagram first to enable collaboration"); return; }
            setCollabEnabled(v => !v);
            setShowCollabPanel(true);
          }}
          title="Enable real-time collaboration"
          data-testid="collab-btn"
        >
          {collabEnabled && connected ? <Wifi className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
          {collabEnabled ? (connected ? `Live (${peers.length})` : "Connecting...") : "Collab"}
        </Button>

        <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={handleSave} disabled={saving} data-testid="save-btn">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Editor + Preview Split */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {showCodePanel && (
            <>
              <ResizablePanel defaultSize={40} minSize={20}>
                <div className="h-full flex flex-col">
                  <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between shrink-0">
                    <span className="text-[11px] font-medium text-slate-400 uppercase tracking-widest font-body">
                      DSL Code
                    </span>
                    {parseError && (
                      <span className="text-[11px] text-red-500 font-mono" data-testid="parse-error">
                        {parseError}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 overflow-auto" data-testid="dsl-code-editor">
                    <CodeMirror
                      value={code}
                      height="100%"
                      extensions={[dslLanguage]}
                      onChange={(value) => handleCodeChange(value)}
                      theme="light"
                      basicSetup={{
                        lineNumbers: true,
                        highlightActiveLineGutter: true,
                        highlightActiveLine: true,
                        foldGutter: false,
                        autocompletion: false,
                      }}
                      style={{
                        fontSize: "13px",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                      className="h-full"
                    />
                  </div>
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
            </>
          )}
          <ResizablePanel defaultSize={60} minSize={30}>
            <div className="h-full flex flex-col">
              <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between shrink-0">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-widest font-body">
                  Preview
                </span>
                <div className="flex items-center gap-2">
                  {addNoteMode && (
                    <span className="text-[10px] text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200 animate-pulse">
                      Click to place note
                    </span>
                  )}
                  {Object.keys(nodeColors).length > 0 && (
                    <button
                      onClick={() => { setNodeColors({}); syncAnnotations(annotations, {}); }}
                      className="text-[10px] text-slate-500 hover:text-slate-700"
                      title="Reset all node colors"
                    >
                      <Palette className="h-3 w-3 inline mr-1" />Reset colors
                    </button>
                  )}
                  {collabEnabled && (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${connected ? "text-green-700 bg-green-50 border-green-200" : "text-amber-600 bg-amber-50 border-amber-200"}`}>
                      {connected ? `${peers.length} peer${peers.length !== 1 ? "s" : ""} online` : "Reconnecting..."}
                    </span>
                  )}
                </div>
              </div>
              <div
                ref={canvasRef}
                className="flex-1 overflow-hidden bg-[#fafbfc] relative"
                style={{
                  backgroundImage: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                  cursor: addNoteMode ? "cell" : "default",
                }}
                onClick={handleCanvasClick}
              >
                <DiagramRenderer
                  graph={parsedGraph}
                  nodeColors={nodeColors}
                  annotations={annotations.filter(a => a.node_id)}
                  onNodeClick={handleNodeClick}
                />
                {/* Free-floating sticky notes */}
                {annotations.filter(a => !a.node_id).map(note => (
                  <StickyNote
                    key={note.id}
                    note={note}
                    onUpdate={handleNoteUpdate}
                    onDelete={handleNoteDelete}
                  />
                ))}
                {/* Peer cursors */}
                {collabEnabled && peers.map(peer => peer.cursor && (
                  <div key={peer.session_id} style={{ position: "absolute", left: peer.cursor.x, top: peer.cursor.y, pointerEvents: "none", zIndex: 200 }}>
                    <div style={{ width: 10, height: 10, background: peer.color, borderRadius: "50% 50% 0 50%", transform: "rotate(45deg)" }} />
                    <span style={{ fontSize: 10, background: peer.color, color: "white", padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap" }}>{peer.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* AI Dialog */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {aiMode === "generate" && <><Sparkles className="h-4 w-4" /> Generate with AI</>}
              {aiMode === "explain" && <><MessageSquare className="h-4 w-4" /> Diagram Explanation</>}
              {aiMode === "improve" && <><Lightbulb className="h-4 w-4" /> Improvement Suggestions</>}
            </DialogTitle>
          </DialogHeader>

          {aiMode === "generate" && (
            <div className="space-y-4">
              <Textarea
                placeholder="Describe the system you want to diagram...&#10;&#10;Example: Create a microservice architecture with API Gateway, authentication, order service, payment service, and PostgreSQL database."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={5}
                className="text-sm font-body"
                data-testid="ai-prompt-input"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowAI(false)}>Cancel</Button>
                <Button size="sm" onClick={() => handleAIAction("generate")} disabled={aiLoading} data-testid="ai-generate-btn">
                  {aiLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : "Generate Diagram"}
                </Button>
              </div>
            </div>
          )}

          {(aiMode === "explain" || aiMode === "improve") && (
            <div className="space-y-3">
              {/* Loader */}
              {aiLoading && (
                <div className="py-10 flex flex-col items-center gap-3 text-slate-500" data-testid="ai-loading">
                  <span className="h-7 w-7 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                  <span className="text-sm font-body">
                    {aiMode === "explain" ? "Reading your diagram..." : "Analyzing for improvements..."}
                  </span>
                </div>
              )}

              {/* Result */}
              {!aiLoading && aiResult && (
                <div
                  className="max-h-[360px] overflow-y-auto p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-body"
                  data-testid="ai-result"
                >
                  {aiResult}
                </div>
              )}

              {/* Previous result notice — shows while loading a fresh result */}
              {aiLoading && aiHistory[aiMode] && (
                <details className="text-xs text-slate-400">
                  <summary className="cursor-pointer hover:text-slate-600">Show previous result</summary>
                  <div className="mt-2 p-3 bg-slate-50 rounded-md whitespace-pre-wrap leading-relaxed border border-slate-200">
                    {aiHistory[aiMode]}
                  </div>
                </details>
              )}

              {/* Empty state — no result yet and not loading */}
              {!aiLoading && !aiResult && (
                <div className="py-8 text-center text-slate-400 text-sm font-body" data-testid="ai-empty">
                  No result yet
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAIAction(aiMode)}
                  disabled={aiLoading}
                  data-testid="ai-rerun-btn"
                  className="gap-1.5 text-xs"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {aiLoading ? "Running..." : "Re-run"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowAI(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog - PDF added */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Export Diagram</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-20 flex-col gap-1.5"
              onClick={() => handleExport("svg")}
              data-testid="export-svg-btn"
            >
              <FileDown className="h-5 w-5 text-slate-600" />
              <span className="text-xs font-medium">SVG</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-1.5"
              onClick={() => handleExport("png")}
              data-testid="export-png-btn"
            >
              <FileDown className="h-5 w-5 text-slate-600" />
              <span className="text-xs font-medium">PNG</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-1.5"
              onClick={() => handleExport("pdf")}
              data-testid="export-pdf-btn"
            >
              <FileDown className="h-5 w-5 text-slate-600" />
              <span className="text-xs font-medium">PDF</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-1.5"
              onClick={() => handleExport("json")}
              data-testid="export-json-btn"
            >
              <FileDown className="h-5 w-5 text-slate-600" />
              <span className="text-xs font-medium">JSON</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Share Diagram</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
              <div>
                <p className="text-sm font-medium text-slate-900">Public Access</p>
                <p className="text-xs text-slate-500">Anyone with the link can view</p>
              </div>
              <Button
                variant={isPublic ? "default" : "outline"}
                size="sm"
                onClick={handleToggleShare}
                data-testid="toggle-share-btn"
              >
                {isPublic ? <Eye className="h-3.5 w-3.5 mr-1.5" /> : <EyeOff className="h-3.5 w-3.5 mr-1.5" />}
                {isPublic ? "Public" : "Private"}
              </Button>
            </div>
            {isPublic && shareUrl && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input value={shareUrl} readOnly className="text-xs font-mono" data-testid="share-url-input" />
                  <Button variant="outline" size="icon" onClick={handleCopyLink} data-testid="copy-share-link-btn">
                    {copied ? <Copy className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500">Collaboration Link</p>
                  <div className="flex gap-2">
                    <Input value={shareUrl} readOnly className="text-xs font-mono" data-testid="collab-url-input" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Collab link copied!"); }} data-testid="copy-collab-link-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400">Share this link to let others collaborate in real-time.</p>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500">Embed Widget</p>
                  <div className="flex gap-2">
                    <Input
                      value={embedCode}
                      readOnly
                      className="text-[11px] font-mono"
                      data-testid="embed-code-input"
                    />
                    <Button variant="outline" size="icon" onClick={handleCopyEmbed} data-testid="copy-embed-code-btn">
                      {embedCopied ? <Copy className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400">Paste this code in any webpage to embed this diagram.</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Collaboration Panel */}
      {showCollabPanel && (
        <div data-testid="collab-panel" style={{ position: "fixed", bottom: 24, right: 24, width: 300, background: "white", border: "1px solid #e2e8f0", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.12)", zIndex: 300, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 14, fontFamily: "Outfit, sans-serif" }}>Live Collaboration</span>
            <button onClick={() => setShowCollabPanel(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={16} /></button>
          </div>

          {/* Connection status */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", background: connected ? "#f0fdf4" : "#fffbeb", borderRadius: 8, border: `1px solid ${connected ? "#86efac" : "#fcd34d"}` }}>
            {connected ? <Wifi size={14} color="#22c55e" /> : <WifiOff size={14} color="#f59e0b" />}
            <span style={{ fontSize: 12, color: connected ? "#15803d" : "#92400e" }}>{connected ? "Connected — changes sync live" : "Connecting..."}</span>
          </div>

          {/* Permission toggle */}
          <div style={{ marginBottom: 14, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: 0, fontFamily: "Manrope, sans-serif" }}>Allow collaborators to edit notes</p>
                <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0", fontFamily: "Manrope, sans-serif" }}>Viewers can add, edit and delete sticky notes</p>
              </div>
              <button
                data-testid="collab-notes-permission-toggle"
                onClick={() => {
                  const next = !collabCanEditNotes;
                  setCollabCanEditNotes(next);
                  sendPermissionUpdate(next);
                  // Also persist in DB
                  if (diagramId) diagramsApi.update(diagramId, { collab_can_edit_notes: next }).catch(() => {});
                }}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                  background: collabCanEditNotes ? "#22c55e" : "#e2e8f0",
                  position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 12,
                }}
              >
                <span style={{
                  position: "absolute", top: 3, left: collabCanEditNotes ? 23 : 3,
                  width: 18, height: 18, borderRadius: "50%", background: "white",
                  transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
              </button>
            </div>
          </div>

          {/* Peers list */}
          {peers.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontFamily: "Manrope, sans-serif" }}>Active collaborators ({peers.length})</p>
              {peers.map(peer => (
                <div key={peer.session_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: peer.color, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 600 }}>
                    {peer.name?.[0]?.toUpperCase() || "G"}
                  </div>
                  <div>
                    <span style={{ fontSize: 13, color: "#334155", fontFamily: "Manrope, sans-serif" }}>{peer.name}</span>
                    <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 6, fontFamily: "Manrope, sans-serif" }}>viewer</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>No collaborators online yet. Share the invite link below.</p>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/view/${diagramId}`); toast.success("Collab link copied!"); }}
              style={{ flex: 1, padding: "8px 0", background: "#0f172a", color: "white", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Manrope, sans-serif" }}
              data-testid="copy-collab-invite-btn"
            >
              Copy invite link
            </button>
            <button
              onClick={() => { setCollabEnabled(false); setShowCollabPanel(false); }}
              style={{ padding: "8px 12px", background: "white", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, cursor: "pointer" }}
            >
              Leave
            </button>
          </div>
        </div>
      )}

      {/* Node Color Picker */}
      {colorPicker && (
        <NodeColorPicker
          x={colorPicker.x}
          y={colorPicker.y}
          currentColor={nodeColors[colorPicker.nodeId]}
          onSelect={handleColorSelect}
          onClose={() => setColorPicker(null)}
        />
      )}
    </div>
  );
}
